import { useEffect, useMemo, useRef, useState } from 'react';
import type { RoomView, RoomVisibility } from '@shared/protocol';
import { GlobeIcon } from './icons';
import { useConfirm } from './ConfirmDialog';

interface Props {
  room: RoomView;
  /** Signed-in owner, excluded from the picker — they always have access. */
  meEmail?: string;
  onShare: (email: string) => void;
  onUnshare: (email: string) => void;
  onVisibility: (visibility: RoomVisibility) => void;
  onDelete: () => void;
  onClose: () => void;
}

/** Local part of an address, for a label that fits the row. */
function shortName(email: string): string {
  return email.split('@')[0]!.replace(/[._]/g, ' ');
}

export function ShareDialog({
  room,
  meEmail,
  onShare,
  onUnshare,
  onVisibility,
  onDelete,
  onClose,
}: Props) {
  const [directory, setDirectory] = useState<string[]>([]);
  const [filter, setFilter] = useState('');
  const closeRef = useRef<HTMLButtonElement>(null);
  const confirm = useConfirm();

  useEffect(() => {
    closeRef.current?.focus();
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Sharing with someone who cannot sign in does nothing, so the dialog
  // offers the access list rather than a free-text address box.
  useEffect(() => {
    let cancelled = false;
    fetch('/api/directory', { credentials: 'same-origin' })
      .then((r) => (r.ok ? r.json() : null))
      .then((body: { people?: string[] } | null) => {
        if (!cancelled && body?.people) setDirectory(body.people);
      })
      .catch(() => {
        /* the dialog still works; it just cannot suggest anyone */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const open = room.visibility === 'everyone';
  const shared = new Set(room.sharedWith);

  const candidates = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return directory
      .filter((e) => e !== meEmail)
      .filter((e) => !q || e.includes(q))
      .sort((a, b) => Number(shared.has(b)) - Number(shared.has(a)) || a.localeCompare(b));
  }, [directory, filter, meEmail, room.sharedWith.join(',')]);

  return (
    <div className="scrim" role="dialog" aria-modal="true" aria-labelledby="share-title">
      <div className="share">
        <div className="share__head">
          <div>
            <div className="share__title" id="share-title">
              Share “{room.name}”
            </div>
            <div className="share__sub">
              Anyone you add can send prompts and approve tools in this room.
            </div>
          </div>
          <button className="share__close" ref={closeRef} onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>

        <button
          className="share__everyone"
          aria-pressed={open}
          onClick={() => onVisibility(open ? 'private' : 'everyone')}
        >
          <span className="share__everyone-badge">
            <GlobeIcon size={15} />
          </span>
          <span className="share__everyone-text">
            <strong>Everyone at Cowork</strong>
            <span>
              {open
                ? 'On — every signed-in person can open this room.'
                : 'Off — only you and the people below.'}
            </span>
          </span>
          <span className={`switch ${open ? 'switch--on' : ''}`} aria-hidden>
            <span className="switch__knob" />
          </span>
        </button>

        <input
          className="share__filter"
          placeholder="Find someone…"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
        />

        <div className="share__list">
          {candidates.length === 0 && (
            <p className="share__empty">
              {directory.length === 0
                ? 'Could not load the directory.'
                : 'Nobody matches that.'}
            </p>
          )}

          {candidates.map((email) => {
            const has = shared.has(email);
            return (
              <div className="share__person" key={email}>
                <span className="share__person-text">
                  <strong>{shortName(email)}</strong>
                  <span>{email}</span>
                </span>
                <button
                  className={`share__toggle ${has ? 'share__toggle--on' : ''}`}
                  onClick={() => (has ? onUnshare(email) : onShare(email))}
                  disabled={open}
                  title={
                    open
                      ? 'This room is open to everyone, so individual access is moot.'
                      : undefined
                  }
                >
                  {has ? 'Remove' : 'Add'}
                </button>
              </div>
            );
          })}
        </div>

        <div className="share__foot">
          {open
            ? 'Turn “Everyone” off to go back to naming people individually.'
            : `${room.sharedWith.length} ${
                room.sharedWith.length === 1 ? 'person' : 'people'
              } besides you.`}
          <button
            className="share__delete"
            onClick={async () => {
              // Artifacts go with the room, and people reach them from "My
              // artifacts" without ever opening it, so say how many.
              const count = await fetch(`/api/rooms/${encodeURIComponent(room.id)}/artifacts`, {
                credentials: 'same-origin',
              })
                .then((r) => (r.ok ? r.json() : undefined))
                .then((b?: { artifacts?: unknown[] }) => b?.artifacts?.length)
                .catch(() => undefined);
              const ok = await confirm({
                tone: 'danger',
                title: `Delete “${room.name}”?`,
                body: 'Its conversation, files and folder are removed for everyone. This cannot be undone.',
                detail:
                  count === 0
                    ? undefined
                    : `${
                        count === undefined
                          ? 'Every artifact published in it'
                          : `${count} artifact${count === 1 ? '' : 's'}`
                      } will be deleted too, from their owners’ My artifacts as well. Download any you want to keep first.`,
                confirmLabel: 'Delete room',
              });
              if (ok) onDelete();
            }}
          >
            Delete room
          </button>
        </div>
      </div>
    </div>
  );
}
